import _ from 'lodash';
import {
  OrderCategory,
  SecurityType,
  OrderSyndicateMember
} from '@ipreo/muni-shared';
import { GameDayModel } from '../../models/gameday-model';
import { BarChartLegend } from '../../shared/models/charts/chart-data-types';
import { noRoundPercents } from '../../profile/pipes/no-round-percents';
import { colors } from '../colors';
import {
  OrdersGroup,
  OrdersSegment,
  SegmentGroupExtractor
} from '../data/gameDay-data.segmentations';
import { GameDayViewType } from '../../state/data/state';
import { Part } from '../data/gameDay-data.transformations';
import {
  orderIsLowPriorityType,
  orderIsRetailType
} from '../data/gameDay-data-orders.calculation';
import {
  retailOtherColor,
  retailColor,
  priorityStateColors,
  priorityNationalColor,
  retailStateColors,
  retailNationalColors,
  managerRolesColors
} from '../bar-chart.colors';

export type IdGenerator = (
  category: OrderCategory,
  priority: GameDayModel.OrderPriority,
  isLowPriority: boolean
) => string;

export type LegendExtractor = (
  deal: GameDayModel.Deal,
  orders: GameDayModel.Order[]
) => BarChartLegend[];

export type ExtendedLegendExtractor = (
  deal: GameDayModel.Deal,
  orders: GameDayModel.Order[],
  idGenerator: IdGenerator
) => BarChartLegend[];

export type ColorExtractor = (group: OrdersGroup) => string;

export type ColorPartExtractor = (part: Part) => string;

export type SizeExtractor<T> = (segment: OrdersSegment<T>) => number;

export type LabelExtractor<T> = (segment: OrdersSegment<T>) => string[];

export interface TrancheExtractor {
  top: LabelExtractor<any>;
  bottom: LabelExtractor<any>;
  size: SizeExtractor<any>;
}

export const AMTSegmentExtractor: SegmentGroupExtractor<{ amt: boolean }> = {
  id: (maturity: GameDayModel.Maturity) => `AMT_${maturity.series.isAMT}`,
  value: (maturity: GameDayModel.Maturity) => ({
    amt: maturity.series.isAMT
  }),
  sortComparator: (
    segment1: OrdersSegment<{ amt: boolean }>,
    segment2: OrdersSegment<{ amt: boolean }>
  ) => {
    const amt1 = segment1.value.amt,
      amt2 = segment2.value.amt;
    if (amt1 === amt2) {
      return 0;
    }
    return segment1.value.amt === true ? 1 : -1;
  }
};

export const nationalGroupCode = 'NA';
export const bondTypes = [
  SecurityType.bond,
  SecurityType.option,
  SecurityType.put,
  SecurityType.zero,
  SecurityType.note
];
export const bondTypesLabels = ['', 'PUT', 'PUT', 'ZERO', 'NOTE'];

export const bondTypeSegmentExtractor: SegmentGroupExtractor<{
  bondType: SecurityType;
}> = {
  id: (maturity: GameDayModel.Maturity) => 'bondType_' + maturity.securityType,
  value: (maturity: GameDayModel.Maturity) => ({
    bondType: maturity.securityType
  }),
  sortComparator: (
    segment1: OrdersSegment<{ bondType: SecurityType }>,
    segment2: OrdersSegment<{ bondType: SecurityType }>
  ) => {
    const bondType1 = segment1.value.bondType;
    const bondType2 = segment2.value.bondType;

    if (bondType1 === bondType2) {
      return 0;
    }

    const index1 = bondTypes.indexOf(segment1.value.bondType);
    const index2 = bondTypes.indexOf(segment2.value.bondType);

    return index1 - index2;
  }
};

export const maturityDateSegmentExtractor: SegmentGroupExtractor<{
  date: Date;
}> = {
  id: (maturity: GameDayModel.Maturity) =>
    [
      'maturityDate_',
      maturity.maturityDate
        .toDate()
        .getTime()
        .toString()
    ].join(''),
  value: (maturity: GameDayModel.Maturity): { date: Date } => {
    const date = maturity.maturityDate.toDate();

    return { date };
  },
  sortComparator: (
    segment1: OrdersSegment<{ date: Date }>,
    segment2: OrdersSegment<{ date: Date }>
  ) => {
    return segment1.value.date.getTime() - segment2.value.date.getTime();
  }
};

export const seriesSegmentExtractor: SegmentGroupExtractor<{
  seriesId: string;
}> = {
  id: (maturity: GameDayModel.Maturity) => 'series_' + maturity.seriesId,
  value: (maturity: GameDayModel.Maturity) => ({
    seriesId: maturity.seriesId
  }),
  sortComparator: (
    segment1: OrdersSegment<{ seriesId: string }>,
    segment2: OrdersSegment<{ seriesId: string }>
  ) => {
    return (
      parseInt(segment1.value.seriesId, 10) -
      parseInt(segment2.value.seriesId, 10)
    );
  }
};

export const maturityIdSegmentExtractor: SegmentGroupExtractor<{
  id: string;
}> = {
  id: (maturity: GameDayModel.Maturity) => 'maturityId_' + maturity.id,
  value: (maturity: GameDayModel.Maturity) => ({ id: maturity.id }),
  sortComparator: (
    segment1: OrdersSegment<{ id: string }>,
    segment2: OrdersSegment<{ id: string }>
  ) => {
    return parseInt(segment1.value.id, 10) - parseInt(segment2.value.id, 10);
  }
};

interface CouponComparisonValue {
  coupon: number;
  insurerName: string;
  yieldPrice: number;
  maturityId: string;
}

export const couponSegmentExtractor: SegmentGroupExtractor<
  CouponComparisonValue
> = {
  id: (maturity: GameDayModel.Maturity) =>
    [
      'coupon_',
      maturity.coupon,
      '_yieldPrice_',
      maturity.yield,
      '_insurerName_',
      maturity.insurer,
      '_maturityId_',
      maturity.id
    ].join(''),
  value: (maturity: GameDayModel.Maturity) => {
    const coupon = maturity.coupon;
    const yieldPrice = maturity.yield;
    const insurerName = maturity.insurer || '';
    const maturityId = maturity.id;

    return { coupon, insurerName, yieldPrice, maturityId };
  },
  sortComparator: (
    segment1: OrdersSegment<CouponComparisonValue>,
    segment2: OrdersSegment<CouponComparisonValue>
  ) => {
    const coupon = (segment1.value.coupon || 0) - (segment2.value.coupon || 0);
    if (coupon !== 0) {
      return coupon;
    }

    const yieldPrice = segment1.value.yieldPrice - segment2.value.yieldPrice;
    if (yieldPrice !== 0) {
      return yieldPrice;
    }

    const insurerName = segment1.value.insurerName.localeCompare(
      segment2.value.insurerName
    );
    if (insurerName !== 0) {
      return insurerName;
    }

    return (
      parseInt(segment1.value.maturityId, 10) -
      parseInt(segment2.value.maturityId, 10)
    );
  }
};

export function getIdByPriorityType(
  category: OrderCategory,
  priority: number,
  isLowPriority: boolean
): string {
  const categoryCode = isLowPriority
    ? 'low'
    : (category && category.groupCode) || '';
  return `priority_${categoryCode}_${priority}`;
}

export function getIdByRetailType(
  category: OrderCategory,
  priority: GameDayModel.OrderPriority,
  isLowPriority: boolean
): string {
  const categoryCode = isLowPriority
    ? 'low'
    : (category && category.code) || '';
  return `retail_${categoryCode}_${priority}`;
}

export function getIdByInstitutionalType(
  category: OrderCategory,
  priority: GameDayModel.OrderPriority,
  isLowPriority: boolean
): string {
  const categoryCode = isLowPriority
    ? 'low'
    : (category && category.groupCode) || '';
  return `institutional_${categoryCode}_${priority}`;
}

function getIdByManagerType(
  role: string,
  departmentId?: number,
  syndicate?: GameDayModel.SyndicateMember[]
): string {
  let parentRole: string;
  if (departmentId && syndicate) {
    parentRole = syndicate.find(
      (manager: GameDayModel.SyndicateMember) => manager.id === departmentId
    ).role;
  }
  return `manager_${role || parentRole || ''}`;
}

export function getIdByMoneyType(moneyTypeCode: string): string {
  return `money_${moneyTypeCode || ''}`;
}

function isRetailOrderWithoutCategory(order: GameDayModel.Order) {
  return order.retailInd && (!order.category || !order.category.name);
}

interface PriorityExtractorValue {
  category: OrderCategory;
  priority: GameDayModel.OrderPriority;
}

export const basePriorityTypeGroupExtractor: SegmentGroupExtractor<
  PriorityExtractorValue
> = {
  id: (order: GameDayModel.Order) =>
    getIdByPriorityType(
      order.category,
      order.priority,
      orderIsLowPriorityType(order)
    ),
  value: (order: GameDayModel.Order) => {
    const category = order.category;
    const priority = order.priority;
    return { priority, category };
  },
  sortComparator: (group1: OrdersGroup, group2: OrdersGroup) => {
    const groupCode1 = group1.value.category.groupCode || '';
    const groupCode2 = group2.value.category.groupCode || '';
    if (group1.value.priority === group2.value.priority) {
      if (groupCode1 === nationalGroupCode) {
        return 1;
      }
      if (groupCode2 === nationalGroupCode) {
        return -1;
      }
      return groupCode1.localeCompare(groupCode2);
    }
    switch (group1.value.priority) {
      case GameDayModel.OrderPriority.retail:
        return -1;
      case GameDayModel.OrderPriority.stock:
        return 1;
      default:
        return 1;
      case GameDayModel.OrderPriority.institutional:
        switch (group2.value.priority) {
          case GameDayModel.OrderPriority.retail:
            return 1;
          case GameDayModel.OrderPriority.stock:
            return -1;
          default:
            return 1;
        }
    }
  }
};

export const priorityTypeGroupExtractor = {
  ...basePriorityTypeGroupExtractor
};
export const priorityTypeWithCategoriesGroupExtractor: SegmentGroupExtractor<
  PriorityExtractorValue
> = {
  ...basePriorityTypeGroupExtractor,
  id: (order: GameDayModel.Order) =>
    getIdByPriorityType(order.category, order.priority, false)
};
export const institutionalTypeGroupExtractor: SegmentGroupExtractor<
  PriorityExtractorValue
> = {
  ...basePriorityTypeGroupExtractor,
  id: (order: GameDayModel.Order) =>
    getIdByInstitutionalType(
      order.category,
      order.priority,
      orderIsLowPriorityType(order)
    )
};
export const institutionalTypeWithCategoriesGroupExtractor: SegmentGroupExtractor<
  PriorityExtractorValue
> = {
  ...basePriorityTypeGroupExtractor,
  id: (order: GameDayModel.Order) =>
    getIdByInstitutionalType(order.category, order.priority, false)
};

export const retailTypeGroupExtractor: SegmentGroupExtractor<
  PriorityExtractorValue
> = {
  id: (order: GameDayModel.Order) =>
    getIdByRetailType(
      order.category,
      order.priority,
      orderIsLowPriorityType(order)
    ),
  value: (
    order: GameDayModel.Order
  ): {
    category: OrderCategory;
    priority: GameDayModel.OrderPriority;
  } => {
    const category = order.category;
    const priority = order.priority;
    return { priority, category };
  },
  mapper: (group: OrdersGroup): OrdersGroup => {
    group.orders = group.orders.filter(orderIsRetailType);
    return group;
  },
  sortComparator: (group1: OrdersGroup, group2: OrdersGroup) => {
    const groupCode1 = group1.value.category.groupCode;
    const groupCode2 = group2.value.category.groupCode;
    const categoryPriority1 = group1.value.category.priority || 0;
    const categoryPriority2 = group2.value.category.priority || 0;

    if (groupCode1 === groupCode2) {
      return categoryPriority1 - categoryPriority2;
    }

    if (!groupCode1 && !!groupCode2) {
      return -1;
    }

    if (!!groupCode1 && !groupCode2) {
      return 1;
    }

    if (groupCode1 === nationalGroupCode) {
      return 1;
    }
    if (groupCode2 === nationalGroupCode) {
      return -1;
    }

    return 0;
  }
};

export const retailTypeWithCategoriesGroupExtractor: SegmentGroupExtractor<
  PriorityExtractorValue
> = {
  ...retailTypeGroupExtractor,
  id: (order: GameDayModel.Order) =>
    getIdByRetailType(order.category, order.priority, false)
};

export const MANAGER_ROLE_SENIOR_MANAGER = 'Senior Manager';

const managerRoles = [
  MANAGER_ROLE_SENIOR_MANAGER,
  'Co-Senior Manager',
  'Co-Manager',
  'Selling Group Member',
  'Member'
];

interface MoneyTypeDesc {
  code: string;
  name: string;
  color: string;
  rank?: number;
}

const stockMoneyTypeKey = 'stock';
const institutionalMoneyTypeKey = 'INT';
const individualsMoneyTypeKey = 'IAN';
const otherMoneyTypeKey = 'OTH';
const brockerDealerMoneyTypeKey = 'BRK';

const moneyTypes = new Map<string, MoneyTypeDesc>();
[
  {
    code: individualsMoneyTypeKey,
    name: 'Individuals',
    color: '#24A356'
  },
  {
    code: 'BPW',
    name: 'Bank Trust/PWM',
    color: '#166435'
  },
  {
    code: 'SMA',
    name: 'SMA',
    color: '#3FD57A'
  },
  {
    code: institutionalMoneyTypeKey,
    name: 'Institutional',
    color: '#55A5E7'
  },
  {
    code: 'IST',
    name: 'Money Manager',
    color: '#000099'
  },
  {
    code: 'MFD',
    name: 'Bond Fund',
    color: '#14578F'
  },
  {
    code: 'ETF',
    name: 'ETF',
    color: '#BBDBF5'
  },
  {
    code: 'IND',
    name: 'Index Fund',
    color: '#11D3EC'
  },
  {
    code: 'INS',
    name: 'Insurance',
    color: '#8946EC'
  },
  {
    code: 'BIP',
    name: 'Bank Portfolio',
    color: '#B58BF3'
  },
  {
    code: 'MMT',
    name: 'MMF',
    color: '#E48FE7'
  },
  {
    code: 'PTY',
    name: 'Prop/Trading',
    color: colors.yellow.primary
  },
  {
    code: 'HFD',
    name: 'Hedge Fund',
    color: '#FF954D'
  },
  {
    code: brockerDealerMoneyTypeKey,
    name: 'Broker/Dealer',
    color: colors.orange.darker
  },
  {
    code: 'GOV',
    name: 'Municipal/Gov',
    color: '#FFFF00'
  },
  {
    code: 'CTY',
    name: 'Corporate',
    color: colors.yellow.dark
  },
  {
    code: 'PEN',
    name: 'Pension',
    color: '#FFFF99'
  },
  {
    code: otherMoneyTypeKey,
    name: 'Other',
    color: '#996633'
  },
  {
    code: stockMoneyTypeKey,
    name: 'Stock',
    color: '#D43D00'
  }
].forEach((desc: MoneyTypeDesc, i: number) => {
  desc.rank = i;
  moneyTypes.set(desc.code, desc);
});

export function getMoneyTypeCodeByAccountPriority(
  order: GameDayModel.Order
): string {
  switch (order.accountInfo.priority) {
    case GameDayModel.AccountPriority.undisclosed:
      return institutionalMoneyTypeKey;
    case GameDayModel.AccountPriority.stock:
      return stockMoneyTypeKey;
    case GameDayModel.AccountPriority.individuals:
      return individualsMoneyTypeKey;
    case GameDayModel.AccountPriority.brockerDealer:
      return brockerDealerMoneyTypeKey;
    case GameDayModel.AccountPriority.regular:
      return getMoneyTypeCodeByOrderPriority(order);
    default:
      throw new Error(
        `Cannot determine money type with account priority ${GameDayModel
          .OrderPriority[<number>order.accountInfo.priority] ||
          order.accountInfo.priority}`
      );
  }
}

export function getMoneyTypeCodeByOrderPriority(
  order: GameDayModel.Order
): string {
  switch (order.priority) {
    case GameDayModel.OrderPriority.institutional:
      return institutionalMoneyTypeKey;
    case GameDayModel.OrderPriority.stock:
      return stockMoneyTypeKey;
    case GameDayModel.OrderPriority.retail:
      return individualsMoneyTypeKey;
    default:
      throw new Error(
        `Cannot determine money type with priority ${GameDayModel.OrderPriority[
          <number>order.priority
        ] || order.priority}`
      );
  }
}

export function getMoneyTypeCode(order: GameDayModel.Order): string {
  const code = order.moneyType && order.moneyType.code;
  if (code) {
    if (!moneyTypes.has(code)) {
      return otherMoneyTypeKey;
    }
    return order.moneyType.code;
  }
  return getMoneyTypeCodeByAccountPriority(order);
}

function groupByMoneyTypeSortComparer(
  group1: OrdersGroup,
  group2: OrdersGroup
): number {
  return moneyTypes.get(group1.value).rank - moneyTypes.get(group2.value).rank;
}

export const moneyTypeGroupExtractor: SegmentGroupExtractor<string> = {
  id: (order: GameDayModel.Order) => getIdByMoneyType(getMoneyTypeCode(order)),
  value: (order: GameDayModel.Order) => getMoneyTypeCode(order),
  sortComparator: groupByMoneyTypeSortComparer
};

export const moneyTypeNoAccountAccessGroupExtractor: SegmentGroupExtractor<
  string
> = {
  id: (order: GameDayModel.Order) =>
    getIdByMoneyType(getMoneyTypeCodeByAccountPriority(order)),
  value: (order: GameDayModel.Order) =>
    getMoneyTypeCodeByAccountPriority(order),
  sortComparator: groupByMoneyTypeSortComparer
};

const getLegendByMoneyTypeCode = (code: string): BarChartLegend => {
  const desc = moneyTypes.get(code);
  return {
    id: getIdByMoneyType(code),
    value: undefined,
    name: desc.name,
    color: desc.color,
    selected: undefined
  };
};

export function byMoneyTypeLegendExtractor(
  deal: GameDayModel.Deal,
  orders: GameDayModel.Order[]
): BarChartLegend[] {
  const codeMapper = deal.gamedaySettings.showAccountNames
    ? getMoneyTypeCode
    : getMoneyTypeCodeByAccountPriority;
  return _(orders)
    .map(codeMapper)
    .uniq()
    .sortBy([code => moneyTypes.get(code).rank])
    .map(getLegendByMoneyTypeCode)
    .value();
}

export const managerTypeGroupExtractor = ({
  deal: { syndicate }
}: GameDayModel.RawData): SegmentGroupExtractor<{
  manager: OrderSyndicateMember;
}> => {
  return {
    id: ({ syndicateDepartment: { role, id } }: GameDayModel.Order) =>
      getIdByManagerType(role, id, syndicate),
    value: ({
      syndicateDepartment: { id, name, role }
    }: GameDayModel.Order): {
      manager: OrderSyndicateMember;
    } => ({
      manager: { id, name, role }
    }),
    sortComparator: (group1: OrdersGroup, group2: OrdersGroup) => {
      const role1 = group1.value.manager.role;
      const role2 = group2.value.manager.role;
      return managerRoles.indexOf(role1) - managerRoles.indexOf(role2);
    }
  };
};

export function getColorExtractor(legends: BarChartLegend[]): ColorExtractor {
  return (group: OrdersGroup) => {
    const foundLegend = legends.find(
      (legend: BarChartLegend) => legend.id === group.id
    );
    return foundLegend ? foundLegend.color : colors.special.expected;
  };
}

export function getColorPartExtractor(
  legends: BarChartLegend[]
): ColorPartExtractor {
  return (part: Part) => {
    const foundLegend = legends.find(
      (legend: BarChartLegend) => legend.id === part.id
    );
    return foundLegend ? foundLegend.color : colors.special.expected;
  };
}

export function couponBottomLabelExtractor<T extends { coupon: number }>(
  segment: OrdersSegment<T>
): string[] {
  const coupon = segment.value.coupon;
  return [!!coupon || coupon === 0 ? `${coupon.toFixed(3)}%` : `${coupon}`];
}

export function subscriptionTopLabelExtractor<T>(
  segment: OrdersSegment<T>
): string[] {
  const amount = segment.groups
    // calculation is based on groups not segment
    // to take in account filtering out orders from groups
    .reduce(
      (order: GameDayModel.Order[], group: OrdersGroup) =>
        order.concat(group.orders),
      []
    )
    .reduce((sum: number, order: GameDayModel.Order) => sum + order.amount, 0);
  const size = segment.maturities.reduce(
    (sum: number, maturity: GameDayModel.Maturity) => sum + maturity.size,
    0
  );
  const subscription = amount / size;
  return [
    subscription < 1
      ? noRoundPercents(subscription * 100)
      : subscription.toFixed(1) + 'x'
  ];
}

export function maturityBottomLabelExtractor<T>(
  segment: OrdersSegment<T>
): string[] {
  const maturity = segment.maturities[0];
  const maturityLabel = maturity.maturityDate.format('MM/YY');

  let bondTypeLabel;
  const bondTypeCode = maturity.securityType;
  const AMT = maturity.series.isAMT;
  const TAX = maturity.series.isTaxable;
  if (bondTypeCode === 'Bond') {
    bondTypeLabel = AMT ? 'AMT' : TAX ? 'Tax' : undefined;
  } else {
    bondTypeLabel = bondTypesLabels[bondTypes.indexOf(bondTypeCode)];
  }

  return [maturityLabel, bondTypeLabel];
}

export function sizeExtractor<T>(segment: OrdersSegment<T>): number {
  return segment.maturities.reduce(
    (size: number, maturity: GameDayModel.Maturity) => size + maturity.size,
    0
  );
}

function categorySortByPriorityComparator(
  c1: OrderCategory,
  c2: OrderCategory
): number {
  return c1.priority - c2.priority;
}

export function getLegendByPriorityBaseType(
  category: OrderCategory,
  color: string,
  idGenerator: IdGenerator
): BarChartLegend {
  return {
    name: category.groupCode + ' Retail',
    id: idGenerator(category, GameDayModel.OrderPriority.retail, false),
    color,
    value: undefined,
    selected: undefined
  };
}

export function byPriorityTypeLegendExtractor(
  deal: GameDayModel.Deal,
  orders: GameDayModel.Order[]
): BarChartLegend[] {
  return byPriorityBaseExtractor(deal, orders, getIdByPriorityType);
}

export function byPriorityBaseExtractor(
  deal: GameDayModel.Deal,
  orders: GameDayModel.Order[],
  idGenerator: IdGenerator
): BarChartLegend[] {
  const categories = deal.categories || [];
  const categoriesExist = !!categories.length;

  const retailLegend: BarChartLegend = {
    name: categoriesExist ? 'Retail - Other' : 'Retail',
    id: idGenerator(undefined, GameDayModel.OrderPriority.retail, false),
    value: undefined,
    color: categoriesExist ? retailOtherColor : retailColor,
    selected: undefined
  };
  const institutionalLegend: BarChartLegend = {
    name: 'Institutional',
    id: idGenerator(undefined, GameDayModel.OrderPriority.institutional, false),
    value: undefined,
    color: colors.blue.primary,
    selected: undefined
  };
  const stockLegend: BarChartLegend = {
    name: 'Stock',
    id: idGenerator(undefined, GameDayModel.OrderPriority.stock, false),
    value: undefined,
    color: colors.red.dark,
    selected: undefined
  };

  const result: BarChartLegend[] = [];

  if (!categoriesExist) {
    result.push(retailLegend);

    if (orders.some(orderIsLowPriorityType)) {
      const lowPriorityRetailLegend: BarChartLegend = {
        name: 'Retail - Other',
        id: idGenerator(undefined, GameDayModel.OrderPriority.retail, true),
        value: undefined,
        color: retailOtherColor,
        selected: undefined
      };

      result.push(lowPriorityRetailLegend);
    }
  } else {
    let state: OrderCategory[] = categories.filter(
      category => category.groupCode !== nationalGroupCode
    );
    state = _.uniqWith(
      state,
      (arrVal, otherVal) => arrVal.groupCode === otherVal.groupCode
    );
    const stateLegends = state
      .sort(categorySortByPriorityComparator)
      .map((category: OrderCategory, index: number) =>
        getLegendByPriorityBaseType(
          category,
          priorityStateColors[index],
          idGenerator
        )
      );
    result.push(...stateLegends);

    const nationalCategory: OrderCategory = categories.find(
      category => category.groupCode === nationalGroupCode
    );

    const nationalColor =
      stateLegends.length > 0 ? priorityNationalColor : retailColor;

    if (nationalCategory) {
      result.push(
        getLegendByPriorityBaseType(
          nationalCategory,
          nationalColor,
          idGenerator
        )
      );
    }

    if (
      orders.some(isRetailOrderWithoutCategory) ||
      orders.some(orderIsLowPriorityType)
    ) {
      result.push(retailLegend);
    }
  }

  return result.concat(institutionalLegend, stockLegend);
}

function getLegendByRetailType(
  category: OrderCategory,
  color: string
): BarChartLegend {
  return {
    name: category.name,
    id: getIdByRetailType(category, GameDayModel.OrderPriority.retail, false),
    color,
    value: undefined,
    selected: undefined
  };
}

export function byRetailTypeLegendExtractor(
  deal: GameDayModel.Deal,
  orders: GameDayModel.Order[]
): BarChartLegend[] {
  const categories = deal.categories || [];
  const categoriesExist = !!categories.length;

  const result: BarChartLegend[] = [];

  const retailLegend: BarChartLegend = {
    name: categoriesExist ? 'Retail - Other' : 'Retail',
    id: getIdByRetailType(undefined, GameDayModel.OrderPriority.retail, false),
    value: undefined,
    color: categoriesExist ? retailOtherColor : retailColor,
    selected: undefined
  };

  if (!categories.length) {
    result.push(retailLegend);

    if (orders.some(orderIsLowPriorityType)) {
      const lowPriorityRetailLegend: BarChartLegend = {
        name: 'Retail - Other',
        id: getIdByRetailType(
          undefined,
          GameDayModel.OrderPriority.retail,
          true
        ),
        value: undefined,
        color: retailOtherColor,
        selected: undefined
      };

      result.push(lowPriorityRetailLegend);
    }
  } else {
    const state = categories
      .filter(category => category.groupCode !== nationalGroupCode)
      .sort(categorySortByPriorityComparator)
      .map((category: OrderCategory, index: number) =>
        getLegendByRetailType(category, retailStateColors[index])
      );
    result.push(...state);

    const nationalColors =
      state.length > 0 ? retailNationalColors : priorityStateColors;

    const national = categories
      .filter(category => category.groupCode === nationalGroupCode)
      .sort(categorySortByPriorityComparator)
      .map((category: OrderCategory, index: number) =>
        getLegendByRetailType(category, nationalColors[index])
      );
    result.push(...national);

    if (
      orders.some(isRetailOrderWithoutCategory) ||
      orders.some(orderIsLowPriorityType)
    ) {
      result.push(retailLegend);
    }
  }

  return result;
}

export function getLegendByManagerRole(
  role: string,
  color: string
): BarChartLegend {
  return {
    id: getIdByManagerType(role),
    value: undefined,
    name: role,
    color,
    selected: undefined
  };
}

export function byManagerTypeLegendExtractor(
  deal: GameDayModel.Deal
): BarChartLegend[] {
  const issueManagerRoles = new Set<string>();
  deal.syndicate.forEach((manager: GameDayModel.SyndicateMember) =>
    issueManagerRoles.add(getIdByManagerType(manager.role))
  );

  return managerRoles
    .map((role: string, index: number) =>
      getLegendByManagerRole(role, managerRolesColors[index])
    )
    .filter((legend: BarChartLegend) => issueManagerRoles.has(legend.id));
}

export function getLegendExtractor(viewType: GameDayViewType): LegendExtractor {
  switch (viewType) {
    case GameDayViewType.PriorityType:
      return byPriorityTypeLegendExtractor;
    case GameDayViewType.RetailType:
      return byRetailTypeLegendExtractor;
    case GameDayViewType.ManagersType:
      return byManagerTypeLegendExtractor;
    case GameDayViewType.MoneyType:
      return byMoneyTypeLegendExtractor;
    case GameDayViewType.InstitutionalType:
      return byInstitutionalTypeLegendExtractor;
    default:
      throw new Error(`Unsupported type ${viewType}`);
  }
}

export function byInstitutionalTypeLegendExtractor(
  deal: GameDayModel.Deal,
  orders: GameDayModel.Order[]
): BarChartLegend[] {
  return byPriorityBaseExtractor(deal, orders, getIdByInstitutionalType);
}

export const segmentsExtractors: SegmentGroupExtractor<any>[] = [
  AMTSegmentExtractor,
  bondTypeSegmentExtractor,
  maturityDateSegmentExtractor,
  seriesSegmentExtractor,
  couponSegmentExtractor
];

export const trancheExtractors: TrancheExtractor[] = [
  {
    top: subscriptionTopLabelExtractor,
    bottom: maturityBottomLabelExtractor,
    size: sizeExtractor
  },
  {
    top: subscriptionTopLabelExtractor,
    bottom: couponBottomLabelExtractor,
    size: sizeExtractor
  }
];

export function getGroupExtractor(
  data: GameDayModel.RawData,
  viewType: GameDayViewType
): SegmentGroupExtractor<any> {
  switch (viewType) {
    case GameDayViewType.PriorityType:
      return data.deal.categories && data.deal.categories.length
        ? priorityTypeWithCategoriesGroupExtractor
        : priorityTypeGroupExtractor;
    case GameDayViewType.RetailType:
      return data.deal.categories && data.deal.categories.length
        ? retailTypeWithCategoriesGroupExtractor
        : retailTypeGroupExtractor;
    case GameDayViewType.ManagersType:
      return managerTypeGroupExtractor(data);
    case GameDayViewType.MoneyType:
      return data.deal.gamedaySettings.showAccountNames
        ? moneyTypeGroupExtractor
        : moneyTypeNoAccountAccessGroupExtractor;
    case GameDayViewType.InstitutionalType:
      return data.deal.categories && data.deal.categories.length
        ? institutionalTypeWithCategoriesGroupExtractor
        : institutionalTypeGroupExtractor;
    default:
      throw new Error(`Unsupported type ${viewType}`);
  }
}
